import React, { useState, useEffect, useRef } from 'react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useNavigate } from 'react-router-dom';
import './3almnyDiscover.css';

const AlmnyDiscover = () => {
  const [currentPlanet, setCurrentPlanet] = useState('earth');
  const [showInfo, setShowInfo] = useState(false);
  const [showSpaceJourney, setShowSpaceJourney] = useState(false);
  const [shuttleStatus, setShuttleStatus] = useState('preparing'); // preparing, launching, orbiting, landing
  const [missionProgress, setMissionProgress] = useState(0);
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();

  const planets = {
    earth: {
      name: 'الأرض',
      description: 'كوكبنا الأزرق الجميل، موطن الحياة والتنوع البيولوجي',
      facts: [
        'المسافة من الشمس: 149.6 مليون كم',
        'عدد الأقمار: 1 (القمر)',
        'مدة اليوم: 24 ساعة',
        'مدة السنة: 365.25 يوم'
      ]
    },
    mars: {
      name: 'المريخ',
      description: 'الكوكب الأحمر، هدف استكشاف الفضاء المستقبلي',
      facts: [
        'المسافة من الشمس: 227.9 مليون كم',
        'عدد الأقمار: 2',
        'مدة اليوم: 24.6 ساعة',
        'مدة السنة: 687 يوم'
      ]
    },
    jupiter: {
      name: 'المشتري',
      description: 'أكبر كوكب في المجموعة الشمسية، عملاق الغاز',
      facts: [
        'المسافة من الشمس: 778.5 مليون كم',
        'عدد الأقمار: 79',
        'مدة اليوم: 9.9 ساعة',
        'مدة السنة: 12 سنة أرضية'
      ]
    }
  };

  // محاكاة حالة المكوك الفضائي
  useEffect(() => {
    const statusInterval = setInterval(() => {
      setShuttleStatus(prev => {
        switch(prev) {
          case 'preparing':
            setMissionProgress(25);
            return 'launching';
          case 'launching':
            setMissionProgress(50);
            return 'orbiting';
          case 'orbiting':
            setMissionProgress(75);
            return 'landing';
          case 'landing':
            setMissionProgress(100);
            return 'preparing';
          default:
            return 'preparing';
        }
      });
    }, 8000);

    return () => clearInterval(statusInterval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const planetKeys = Object.keys(planets);
      const currentIndex = planetKeys.indexOf(currentPlanet);
      const nextIndex = (currentIndex + 1) % planetKeys.length;
      setCurrentPlanet(planetKeys[nextIndex]);
    }, 5000);

    return () => clearInterval(interval);
  }, [currentPlanet]);

  // بدء الرحلة العلمية
  const startScientificJourney = () => {
    setShowSpaceJourney(true);
  };

  // إغلاق الرحلة العلمية
  const closeScientificJourney = () => {
    setShowSpaceJourney(false);
  };

  // ملء الشاشة
  const toggleFullscreen = () => {
    const iframe = document.getElementById('earth-iframe');
    if (iframe) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        iframe.requestFullscreen();
      }
    }
  };

  const handleStartJourney = (event) => {
    
    // إضافة تأثيرات بصرية للزر
    const button = event.target;
    button.style.transform = 'scale(0.95)';
    setTimeout(() => {
      button.style.transform = 'scale(1)';
    }, 150);

    startScientificJourney();
  };

  const getShuttleStatusText = () => {
    switch(shuttleStatus) {
      case 'preparing':
        return 'جاري إعداد المكوك الفضائي...';
      case 'launching':
        return 'إطلاق المكوك الفضائي! 🚀';
      case 'orbiting':
        return 'المكوك في المدار الفضائي 🌌';
      case 'landing':
        return 'العودة للأرض... 🌍';
      default:
        return 'إعداد المهمة...';
    }
  };

  return (
    <div className={`discover-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Space Shuttle Mission Control */}
  

      {/* Earth 360 Journey Scene */}
      {showSpaceJourney && (
        <div className="earth-journey-overlay">
          <div className="journey-header">
            <h2 className="journey-title">رحلة استكشاف الأرض 🌍</h2>
            <div className="journey-controls">
              <button 
                className="btn btn-fullscreen"
                onClick={toggleFullscreen}
                title="ملء الشاشة"
              >
                <i className="fas fa-expand"></i>
              </button>
              <button 
                className="btn btn-close"
                onClick={closeScientificJourney}
                title="إغلاق"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <div className="earth-container">
            <iframe 
              id="earth-iframe"
              src="https://earth.plus360degrees.com/" 
              width="100%" 
              height="100%" 
              style={{ border: 'none' }} 
              allowFullScreen 
              loading="lazy"
              title="Earth 360 View"
            />
          </div>
        </div>
      )}

      {/* Header Section - Space Shuttle Theme */}
      <div className="discover-header">
        <div className="container">
          <div className="discover-header-content">
            <div className="discover-breadcrumb">
              <a href="/" className="breadcrumb-link">الرئيسية</a>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">استكشف المكوك الفضائي</span>
            </div>
            <h1 className="discover-title">🚀 مهمة المكوك الفضائي</h1>
            <p className="discover-description">
              انضم إلى طاقم المكوك الفضائي واستكشف عجائب الكون. رحلة تفاعلية عبر المجموعة الشمسية والمجرات البعيدة.
            </p>
          </div>
        </div>
      </div>

      {/* Space Shuttle Scene */}
      <div className="space-shuttle-scene">
        <div className="shuttle-container">
          <div className="space-shuttle">
            <div className="shuttle-body">
              <div className="shuttle-nose"></div>
              <div className="shuttle-cockpit"></div>
              <div className="shuttle-wings">
                <div className="wing left-wing"></div>
                <div className="wing right-wing"></div>
              </div>
              <div className="shuttle-engines">
                <div className="engine engine-1"></div>
                <div className="engine engine-2"></div>
                <div className="engine engine-3"></div>
              </div>
            </div>
            <div className="shuttle-exhaust">
              <div className="exhaust-flame flame-1"></div>
              <div className="exhaust-flame flame-2"></div>
              <div className="exhaust-flame flame-3"></div>
            </div>
          </div>
        </div>

        {/* Stars Background */}
        <div className="stars-container">
          {[...Array(150)].map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Planets Display */}
        <div className="planets-container">
          <div className={`planet earth ${currentPlanet === 'earth' ? 'active' : ''}`}>
            <div className="planet-sphere"></div>
            <div className="planet-orbit"></div>
          </div>
          <div className={`planet mars ${currentPlanet === 'mars' ? 'active' : ''}`}>
            <div className="planet-sphere"></div>
            <div className="planet-orbit"></div>
          </div>
          <div className={`planet jupiter ${currentPlanet === 'jupiter' ? 'active' : ''}`}>
            <div className="planet-sphere"></div>
            <div className="planet-orbit"></div>
          </div>
        </div>

        {/* Sun */}
        <div className="sun">
          <div className="sun-core"></div>
          <div className="sun-rays"></div>
        </div>
      </div>

      {/* Planet Information Panel */}
      <div className="planet-info-panel">
        <div className="container">
          <div className="info-card">
            <div className="info-header">
              <h2>{planets[currentPlanet].name}</h2>
              <button 
                className="info-toggle-btn"
                onClick={() => setShowInfo(!showInfo)}
              >
                <i className={`fas ${showInfo ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
              </button>
            </div>
            <p className="planet-description">{planets[currentPlanet].description}</p>
            
            {showInfo && (
              <div className="facts-container">
                <h3>حقائق مثيرة</h3>
                <ul className="facts-list">
                  {planets[currentPlanet].facts.map((fact, index) => (
                    <li key={index} className="fact-item">
                      <i className="fact-icon fas fa-rocket"></i>
                      {fact}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Learning Hub - Space Missions */}
      <div className="learning-hub">
        <div className="container">
          <div className="hub-header">
            <h2 className="hub-title">مهام فضائية تفاعلية</h2>
            <p className="hub-subtitle">انضم إلى طاقم المكوك الفضائي واستكشف الكون</p>
          </div>
          
          <div className="hub-grid">
            <div className="hub-card">
              <div className="card-header">
                <div className="card-icon">
                  <i className="fas fa-satellite"></i>
                </div>
                <div className="card-badge">مهمة</div>
              </div>
              <h3>استكشاف القمر</h3>
              <p>رحلة تفاعلية لاستكشاف سطح القمر والبحث عن الموارد الفضائية</p>
              <div className="card-features">
                <span className="feature-tag">قمر</span>
                <span className="feature-tag">استكشاف</span>
                <span className="feature-tag">موارد</span>
              </div>
            </div>
            
            <div className="hub-card">
              <div className="card-header">
                <div className="card-icon">
                  <i className="fas fa-meteor"></i>
                </div>
                <div className="card-badge">استكشاف</div>
              </div>
              <h3>رحلة إلى المريخ</h3>
              <p>مهمة فضائية لاستكشاف الكوكب الأحمر والبحث عن علامات الحياة</p>
              <div className="card-features">
                <span className="feature-tag">مريخ</span>
                <span className="feature-tag">حياة</span>
                <span className="feature-tag">استعمار</span>
              </div>
            </div>
            
            <div className="hub-card">
              <div className="card-header">
                <div className="card-icon">
                  <i className="fas fa-meteor"></i>
                </div>
                <div className="card-badge">اكتشاف</div>
              </div>
              <h3>استكشاف المجرات</h3>
              <p>رحلة إلى المجرات البعيدة واكتشاف أسرار الكون والثقوب السوداء</p>
              <div className="card-features">
                <span className="feature-tag">مجرات</span>
                <span className="feature-tag">كون</span>
                <span className="feature-tag">ثقوب سوداء</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action - Launch Mission */}
      <div className="discover-cta">
        <div className="container">
          <div className="cta-content">
            <h2>🚀 انطلق في مهمة فضائية الآن!</h2>
            <p>انضم إلى طاقم المكوك الفضائي وابدأ رحلة استكشاف الكون</p>
            <div className="cta-buttons">
              <button className="btn btn-primary" onClick={handleStartJourney}>
                <i className="fas fa-rocket"></i>
                إطلاق المهمة
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlmnyDiscover; 